import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FileUploadZone = ({ onFilesSelected, isProcessing }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const acceptedFormats = ['.pdf', '.doc', '.docx'];
  const maxFileSize = 10 * 1024 * 1024; // 10MB

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e?.dataTransfer?.files);
    handleFileSelection(files);
  };

  const handleFileInput = (e) => {
    const files = Array.from(e?.target?.files);
    handleFileSelection(files);
  };

  const handleFileSelection = (files) => {
    const validFiles = files?.filter(file => {
      const isValidFormat = acceptedFormats?.some(format => 
        file?.name?.toLowerCase()?.endsWith(format?.toLowerCase())
      );
      const isValidSize = file?.size <= maxFileSize;
      return isValidFormat && isValidSize;
    });

    if (validFiles?.length > 0) {
      onFilesSelected(validFiles);
    }

    // Reset input
    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleBrowseClick = () => {
    fileInputRef?.current?.click();
  };

  return (
    <div className="w-full">
      <div
        className={`
          relative w-full h-80 border-2 border-dashed rounded-xl
          transition-all duration-300 cursor-pointer
          ${isDragOver 
            ? 'border-primary bg-primary/5 scale-[1.02]' 
            : 'border-border hover:border-primary/50 hover:bg-muted/30'
          }
          ${isProcessing ? 'pointer-events-none opacity-60' : ''}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleBrowseClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx"
          onChange={handleFileInput}
          className="hidden"
        />

        <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center">
          <div className={`
            w-20 h-20 rounded-full flex items-center justify-center mb-6
            transition-all duration-300
            ${isDragOver 
              ? 'bg-primary text-primary-foreground scale-110' 
              : 'bg-muted text-muted-foreground'
            }
          `}>
            <Icon 
              name={isDragOver ? "Upload" : "FileText"} 
              size={32} 
              className={isDragOver ? "animate-bounce" : ""}
            />
          </div>

          <h3 className="text-xl font-semibold text-foreground mb-2">
            {isDragOver ? "Drop files here" : "Upload CV Files"}
          </h3>
          
          <p className="text-muted-foreground mb-6 max-w-md">
            Drag and drop your CV files here, or click to browse. 
            Support for PDF, DOC, and DOCX formats up to 10MB each.
          </p>

          <div className="flex flex-col sm:flex-row items-center gap-4">
            <Button 
              variant="default" 
              iconName="Upload" 
              iconPosition="left"
              disabled={isProcessing}
            >
              Browse Files
            </Button>
            
            <div className="text-sm text-muted-foreground">
              or drag files here
            </div>
          </div>

          <div className="mt-6 flex flex-wrap justify-center gap-2">
            {acceptedFormats?.map((format) => (
              <span 
                key={format}
                className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md font-mono"
              >
                {format?.toUpperCase()}
              </span>
            ))}
          </div>
        </div>

        {isDragOver && (
          <div className="absolute inset-0 bg-primary/10 rounded-xl border-2 border-primary animate-pulse" />
        )}
      </div>
      <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
        <div className="flex items-center space-x-2">
          <Icon name="Info" size={16} />
          <span>Maximum file size: 10MB per file</span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Shield" size={16} />
          <span>Files are processed securely</span>
        </div>
      </div>
    </div>
  );
};

export default FileUploadZone;